import { Box, Button, Card, Heading, HStack, Text, VStack } from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import { ArrowRight, Factory, Server, Truck, Zap } from 'lucide-react'
import type { IcpData } from '@/data/business-value'
import { cardHover } from '~/components/shell'
import AnimatedCounter from './AnimatedCounter'

const iconMap: Record<string, LucideIcon> = {
  Factory,
  Zap,
  Server,
  Truck,
}

interface IcpOverviewCardProps {
  icp: IcpData
}

export default function IcpOverviewCard({ icp }: IcpOverviewCardProps) {
  const navigate = useNavigate()
  const Icon = iconMap[icp.icon] || Factory
  const previewMetrics = icp.keyMetrics.slice(0, 2)

  return (
    <Card.Root
      overflow="hidden"
      transition="all 0.3s"
      _hover={cardHover}
      cursor="pointer"
      onClick={() => navigate({ to: `/business-value/${icp.id}` })}
      h="100%"
    >
      <Box
        h={1.5}
        bgGradient="to-r"
        gradientFrom={icp.gradient.from}
        gradientTo={icp.gradient.to}
      />
      <Card.Body p={6} mt={4}>
        <VStack gap={4} align="start" h="100%">
          <Box p={3} borderRadius="xl" bg={`${icp.color}/10`} color={icp.color}>
            <Icon size={28} />
          </Box>
          <Heading size="md">{icp.title}</Heading>
          <Text fontSize="sm" color="content.secondary" flex={1}>
            {icp.shortDescription}
          </Text>
          <HStack gap={6} w="100%">
            {previewMetrics.map((m, i) => (
              <VStack key={m.label} gap={0} align="start">
                <AnimatedCounter
                  value={m.value}
                  prefix={m.prefix}
                  suffix={m.suffix}
                  className="text-lg font-bold"
                  delay={0.2 * i}
                />
                <Text fontSize="xs" color="content.secondary">
                  {m.label}
                </Text>
              </VStack>
            ))}
          </HStack>
        </VStack>
      </Card.Body>
      <Card.Footer p={4} pt={0}>
        <Button
          w="100%"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation()
            navigate({ to: `/business-value/${icp.id}` })
          }}
        >
          <HStack gap={2}>
            <span>Explore</span>
            <ArrowRight size={16} />
          </HStack>
        </Button>
      </Card.Footer>
    </Card.Root>
  )
}
